'use client';

import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  onStop: () => void;
  subjectId: string | null;
  profileId: string | null;
  onDocumentsChange?: (
    subjectId: string,
    documents: { filename: string; originalName: string; uploadedAt: string }[]
  ) => void;
}

export default function ChatInput({
  onSend,
  isLoading,
  onStop,
  subjectId,
  profileId,
  onDocumentsChange,
}: ChatInputProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleUploadClick = () => {
    if (!profileId || !subjectId) {
      alert('Select a subject in the sidebar before uploading PDFs.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!profileId || !subjectId) {
      alert('Select a subject in the sidebar before uploading PDFs.');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('profileId', profileId);
      formData.append('subjectId', subjectId);

      const res = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Upload failed:', errorText);
        alert('Failed to upload PDF. Please try again.');
        return;
      }

      if (onDocumentsChange) {
        const docsRes = await fetch(
          `/api/documents?profile=${encodeURIComponent(profileId)}&subject=${encodeURIComponent(subjectId)}`
        );
        if (docsRes.ok) {
          const data = await docsRes.json();
          onDocumentsChange(subjectId, data.documents ?? []);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload PDF. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset the file input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="chat-input">
      <div className="chat-input__container">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything you're studying..."
          rows={1}
          className="chat-input__textarea"
          disabled={isLoading}
        />
        <button
          onClick={handleUploadClick}
          className="chat-input__button chat-input__button--upload"
          title={
            subjectId
              ? 'Upload a PDF study document for this subject'
              : 'Select a subject to upload PDFs'
          }
          disabled={isLoading || isUploading}
        >
          {isUploading ? (
            <span className="chat-input__upload-spinner">
              <span />
              <span />
              <span />
            </span>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16.5 6.5L7.5 15.5" />
              <path d="M14 4l4 4" />
              <path d="M8 20h8a3 3 0 0 0 3-3v-3" />
              <path d="M4 7v10a3 3 0 0 0 3 3" />
            </svg>
          )}
        </button>
        {isLoading ? (
          <button onClick={onStop} className="chat-input__button chat-input__button--stop" title="Stop">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!input.trim()}
            className="chat-input__button chat-input__button--send"
            title="Send"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        )}
      </div>
      <p className="chat-input__hint">Shift + Enter for new line</p>
    </div>
  );
}
